import axiosInstance from '../../api/axios.js';
import {
  extractRisksArray,
  normalizeRiskRow,
  normalizeRiskRows,
} from '../../components/risks-table/utils.js';
import type { RiskProcessAssociationFormValue } from '../../features/risks/schema.ts';
import type {
  CreateRiskInput,
  RiskRecord,
  RiskUpsertInput,
  RiskUpsertPayload,
  RiskProcessMappingWire,
  RiskBulkImportBody,
} from './risks.types.ts';

export type {
  CreateRiskInput,
  RiskRecord,
  RiskUpsertInput,
  RiskUpsertPayload,
  RiskProcessMappingWire,
  RiskBulkImportBody,
  RiskBulkImportFileDataRow,
} from './risks.types.ts';

function toProcessMappingsWire(
  rows: RiskProcessAssociationFormValue[] | undefined
): RiskProcessMappingWire[] {
  return (rows ?? [])
    .map((row) => ({
      process_id: row.processId.trim(),
      control_ids: (row.mitigatingControlIds ?? []).map((id) => id.trim()).filter(Boolean),
    }))
    .filter((row) => Boolean(row.process_id));
}

function mappingsPayloadFromForm(
  processAssociations: RiskProcessAssociationFormValue[] | undefined
): Pick<RiskUpsertPayload, 'process_mappings'> {
  return {
    process_mappings: toProcessMappingsWire(processAssociations),
  };
}

function toCreateRiskPayload(payload: CreateRiskInput): RiskUpsertPayload {
  const resolvedRiskId = (payload.clientRiskIdOverride ?? payload.clientRiskId ?? '').trim();
  const mappings = mappingsPayloadFromForm(payload.processAssociations);
  const fid = (payload.fileId ?? '').toString().trim();
  const classification =
    String(payload.classification ?? 'business').trim().toLowerCase() === 'itgc' ? 'itgc' : 'business';
  const domainId = String(payload.itgcDomainId ?? '').trim();

  const body: RiskUpsertPayload = {
    client_risk_id: resolvedRiskId || null,
    risk_category: payload.riskCategory,
    risk_description: payload.riskDescription.trim(),
    risk_level: payload.riskLevel,
    classification,
    ...(fid ? { file_id: fid } : {}),
    ...mappings,
  };

  if (classification === 'itgc') {
    body.itgc_domain_id = domainId || null;
    body.process_mappings = [];
  }

  // Stamp the active audit year so the risk appears in year-scoped lists.
  const auditYearId = String(payload.auditYearId ?? '').trim();
  if (auditYearId) body.audit_year_id = auditYearId;

  return body;
}

function toRiskPatchPayload(payload: RiskUpsertInput): RiskUpsertPayload {
  const patch: RiskUpsertPayload = {};

  if ('clientRiskId' in payload) {
    const resolvedRiskId = (payload.clientRiskId ?? '').trim();
    patch.client_risk_id = resolvedRiskId || null;
  }
  if ('riskCategory' in payload) {
    patch.risk_category = payload.riskCategory;
  }
  if ('riskDescription' in payload) {
    patch.risk_description = (payload.riskDescription ?? '').trim();
  }
  if ('riskLevel' in payload) {
    patch.risk_level = payload.riskLevel;
  }
  if ('processAssociations' in payload) {
    Object.assign(patch, mappingsPayloadFromForm(payload.processAssociations));
  }
  if ('itgcDomainId' in payload) {
    const domainId = String(payload.itgcDomainId ?? '').trim();
    patch.itgc_domain_id = domainId || null;
  }

  return patch;
}

export async function fetchAllProcessRisks(): Promise<RiskRecord[]> {
  const response = await axiosInstance.get('/api/risks');
  const raw = extractRisksArray(response.data);
  return normalizeRiskRows(raw) as RiskRecord[];
}

export async function fetchRiskById(riskId: string, auditYearId?: string | null): Promise<RiskRecord | null> {
  const trimmed = riskId.trim();
  if (!trimmed) return null;

  const params: Record<string, string> = {};
  if (auditYearId) params.audit_year_id = auditYearId;
  const response = await axiosInstance.get(`/api/risks/${encodeURIComponent(trimmed)}`, { params });
  const body = response.data;

  let raw: unknown = body;
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const envelope = body as Record<string, unknown>;
    if (envelope.risk && typeof envelope.risk === 'object') {
      raw = envelope.risk;
    } else if (envelope.data && typeof envelope.data === 'object') {
      raw = envelope.data;
    }
  }

  const normalized = normalizeRiskRow(raw);
  return (normalized as RiskRecord | null) ?? null;
}

export async function createRisk(payload: CreateRiskInput): Promise<unknown> {
  const body = toCreateRiskPayload(payload);
  const response = await axiosInstance.post('/api/risks', body);
  return response.data;
}

export async function updateRisk(riskId: string, payload: RiskUpsertInput, auditYearId?: string): Promise<unknown> {
  const body = toRiskPatchPayload(payload);
  if (auditYearId) body.audit_year_id = auditYearId;
  const response = await axiosInstance.patch(`/api/risks/${riskId}`, body);
  return response.data;
}

/** POST `/api/risk-upload/import` — `file_data` rows use snake_case keys (same shape as validate `file_data`). */
export async function importRisksBulkUpload(body: RiskBulkImportBody): Promise<unknown> {
  const response = await axiosInstance.post('/api/risk-upload/import', body);
  return response.data;
}
